import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import { useTranslation } from "react-i18next"
import { ERequestType } from "../../Enum/ERequestType"
import { GetRequest, OpenPdfFromBase64, PostRequest } from "../../services/ApiClientService"
import { API_ROUTES } from "../../configuration/RoutesApi"
import type { IPrintAgentOverviewDto } from "../../Objects/PrintAutomation/IPrintAgentOverviewDto"
import type { EPrintType } from "../../Enum/EPrintType"

export type PrintCommandType = "kitchen" | "delivery" | "bar" | "all"

export type PrintTargetOption = {
  label: string
  value: string
  printerConfigurationId?: number
}

export const useSavePrint = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const getActivePrintTargets = async (commandType: PrintCommandType = "all"): Promise<PrintTargetOption[]> => {
    try {
      const response = await GetRequest<IPrintAgentOverviewDto[]>(API_ROUTES.PRINT_AUTOMATION.OVERVIEW)

      const downloadOption = { value: "download", label: t("buttons:button_download") }

      const payload = Array.isArray(response as unknown)
        ? (response as unknown as IPrintAgentOverviewDto[])
        : response?.data

      if (!Array.isArray(payload)) {
        return [downloadOption]
      }

      const normalizePrintType = (printType: EPrintType | string | number): EPrintType | null => {
        if (typeof printType === "number") {
          const byNumber: Record<number, EPrintType> = {
            1: "Kitchen",
            2: "Bar",
            3: "Delivery",
            4: "All",
          }

          return byNumber[printType] ?? null
        }

        if (typeof printType !== "string") {
          return null
        }

        const normalized = printType.trim().toLowerCase()
        const aliases: Record<string, EPrintType> = {
          kitchen: "Kitchen",
          bar: "Bar",
          delivery: "Delivery",
          all: "All",
          customer: "Delivery",
        }

        return aliases[normalized] ?? null
      }

      const acceptedPrintTypes: Record<PrintCommandType, EPrintType[]> = {
        kitchen: ["Kitchen", "All"],
        delivery: ["Delivery", "All"],
        bar: ["Bar", "All"],
        all: ["Kitchen", "Bar", "Delivery", "All"],
      }

      const activePrinters = payload.flatMap((agent) => {
        const isValidAgent = !agent.revokedAt

        if (!isValidAgent) {
          return []
        }

        return (agent.printers ?? [])
          .filter(
            (printer) =>
              printer.id != null &&
              printer.isEnabled &&
              printer.isAgentActive &&
              (() => {
                const normalizedPrintType = normalizePrintType(printer.printType)

                return normalizedPrintType != null && acceptedPrintTypes[commandType].includes(normalizedPrintType)
              })(),
          )
          .map((printer) => ({
            value: `printer:${printer.id}`,
            label: `${printer.displayName} • ${agent.name}`,
            printerConfigurationId: printer.id,
          }))
      })

      return [downloadOption, ...activePrinters]
    } catch {
      return [{ value: "download", label: t("buttons:button_download") }]
    }
  }

  const dispatchOrderToSpecificPrinter = async (orderId: number, printerConfigurationId: number) => {
    const response = await PostRequest<object>(
      API_ROUTES.PRINT.TO_PRINTER,
      { orderId, printerConfigurationId },
      ERequestType.Restaurant,
    )

    if (!response.success) {
      throw new Error(response.message)
    }
  }

  const handlePrintOrderToCustomer = async (orderId: number, onSuccess: any, printerConfigurationId?: number) => {
    enableLoad()

    try {
      if (printerConfigurationId && printerConfigurationId > 0) {
        await dispatchOrderToSpecificPrinter(orderId, printerConfigurationId)
        onSuccess?.()
        return
      }

      const params = new URLSearchParams()

      params.append("id", orderId.toString())

      const response = await GetRequest<string>(
        `${API_ROUTES.PRINT.TO_CUSTOMER}?${params.toString()}`,
        ERequestType.Restaurant,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      OpenPdfFromBase64(response.data!, `pedido-${orderId}.pdf`, true)

      onSuccess?.()
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const handlePrintOrderToKitchen = async (orderId: number, onSuccess: any, printerConfigurationId?: number) => {
    enableLoad()

    try {
      if (printerConfigurationId && printerConfigurationId > 0) {
        await dispatchOrderToSpecificPrinter(orderId, printerConfigurationId)
        onSuccess?.()
        return
      }

      const params = new URLSearchParams()

      params.append("id", orderId.toString())

      const response = await GetRequest<string>(
        `${API_ROUTES.PRINT.TO_KITCHEN}?${params.toString()}`,
        ERequestType.Restaurant,
      )

      if (!response.success) {
        throw new Error(response.message)
      }

      OpenPdfFromBase64(response.data!, `pedido-${orderId}.pdf`, true)

      onSuccess?.()
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : t("label_unknown_error_description")

      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  return {
    getActivePrintTargets,
    handlePrintOrderToCustomer,
    handlePrintOrderToKitchen,
  }
}
